import React, { useState, useCallback } from "react";
import {
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  Label,
  Input,
  FormFeedback,
  Button,
  Form,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useEditVehicleStock } from "hooks/stockHook";
import ButtonTooltip from "components/Common/ButtonTooltip";
import { useToastMessage } from "hooks/useToastMessage";

export default function StockDescriptionUpdate({ stock = null }) {
  const [modal, setModal] = useState(false);
  const toggle = () => setModal(!modal);

    const { handleResponse } = useToastMessage();

  const { mutateAsync: editStock, isPending: stockEditLoading } =
    useEditVehicleStock();

  const validationSchema = Yup.object({
    short_descriptions: Yup.string().max(500, "Short description must be less than 500 characters"),
    full_descriptions: Yup.string(),
  });

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        const res = await editStock({ id: stock?.id, ...values });
        handleResponse(res, "Description updated successfully.", true);
        if (res.success) {
          toggle();
          resetForm();
        }
      } catch (error) {
        console.error("Failed to update description:", error);
      }
    },
    [editStock, stock, handleResponse]
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      short_descriptions: stock?.short_descriptions || "",
      full_descriptions: stock?.full_descriptions || "",
    },
    validationSchema,
    onSubmit,
  });

  return (
    <>
      <ButtonTooltip
        type="edit"
        onClick={toggle}
        btnId="edit-description"
        btnName="Edit Description"
      />

      <Modal isOpen={modal} toggle={toggle} centered size="lg">
        <ModalHeader toggle={toggle}>Edit Description</ModalHeader>
        <ModalBody>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
            }}
          >
            <Row>
              <Col md={12}>
                <Label>Short Description</Label>
                <Input
                  type="textarea"
                  rows={3}
                  name="short_descriptions"
                  value={validation.values.short_descriptions}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.short_descriptions}
                  placeholder="Brief description (max 500 characters)"
                />
                <FormFeedback>{validation.errors.short_descriptions}</FormFeedback>
                <div className="text-muted text-end">
                  {validation.values.short_descriptions.length}/500 characters
                </div>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={12}>
                <Label>Full Description</Label>
                <Input
                  type="textarea"
                  rows={6}
                  name="full_descriptions"
                  value={validation.values.full_descriptions}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.full_descriptions}
                  placeholder="Detailed description of the vehicle"
                />
                <FormFeedback>{validation.errors.full_descriptions}</FormFeedback>
              </Col>
            </Row>

            <div className="text-end mt-4">
              <Button
                type="submit"
                color="success"
                disabled={stockEditLoading}
              >
                Save Changes
                {stockEditLoading && "..."}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </>
  );
}